import { request } from '../../../utils/request';

type AuthProps = { authToken: string };

export type WalletTransferPayload = {
  pin: string;
  from_account_number: string;
  to_account_number: string;
  amount: number;
  remarks?: string;
};

export type WalletTransaction = {
  id: number;
  reference: string;
  type: 'topup' | 'wallet_to_wallet' | 'payout_external';
  status: 'pending' | 'processing' | 'successful' | 'failed' | 'reversed';
  amount: string;
  from_account_number?: string | null;
  to_account_number?: string | null;
  remarks?: string | null;
  created_at: string;
};

export type WalletTransactionsResponse = {
  records: WalletTransaction[];
  page: number;
  page_size: number;
};

export type PayoutMethod = {
  id: number;
  type: 'embedly_wallet' | 'embedly_bank' | 'paystack_bank';
  bank_code: string | null;
  bank_name: string | null;
  account_number: string | null;
  account_name: string | null;
  verified_account_name: string | null;
  is_default: boolean;
};

export type ExternalPayoutPayload = {
  pin: string;
  from_account_number: string;
  to_account_number: string;
  to_bank_code: string;
  to_bank_name: string;
  to_account_name: string;
  amount: number;
  remarks?: string;
};

export function getPayoutMethods(props: AuthProps): Promise<PayoutMethod[]> {
  return request({
    url: '/payments/payout-methods',
    method: 'GET',
    headers: { Authorization: `Bearer ${props.authToken}` },
    onError: (error) => {
      throw error;
    },
  });
}

export function externalPayout(
  props: AuthProps & { payload: ExternalPayoutPayload }
) {
  return request({
    url: '/payments/wallet/payout',
    method: 'POST',
    formData: props.payload,
    headers: { Authorization: `Bearer ${props.authToken}` },
    onError: (error) => {
      throw error;
    },
  });
}

export function walletToWalletTransfer(
  props: AuthProps & { payload: WalletTransferPayload }
) {
  return request({
    url: '/payments/wallet/transfer',
    method: 'PUT',
    formData: props.payload,
    headers: { Authorization: `Bearer ${props.authToken}` },
    onError: (error) => {
      throw error;
    },
  });
}

export function setWalletPin(
  props: AuthProps & { pin: string; current_pin?: string }
) {
  return request({
    url: '/payments/wallet/pin',
    method: 'POST',
    formData: {
      pin: props.pin,
      ...(props.current_pin ? { current_pin: props.current_pin } : {}),
    },
    headers: { Authorization: `Bearer ${props.authToken}` },
    onError: (error) => {
      throw error;
    },
  });
}

export function verifyWalletPin(props: AuthProps & { pin: string }) {
  return request({
    url: '/payments/wallet/pin/verify',
    method: 'POST',
    formData: { pin: props.pin },
    headers: { Authorization: `Bearer ${props.authToken}` },
    onError: (error) => {
      throw error;
    },
  });
}

export function getWalletTransactions(
  props: AuthProps & {
    type?: string;
    status?: string;
    page?: number;
    page_size?: number;
  }
): Promise<WalletTransactionsResponse> {
  const params: Record<string, string> = {};
  if (props.type) params.type = props.type;
  if (props.status) params.status = props.status;
  if (props.page) params.page = String(props.page);
  if (props.page_size) params.page_size = String(props.page_size);

  return request({
    url: '/payments/wallet/transactions',
    method: 'GET',
    params,
    headers: { Authorization: `Bearer ${props.authToken}` },
    onError: (error) => {
      throw error;
    },
  });
}
